

import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Spin } from "antd";
// Internal Imports
import { protectedRoutes } from "../utils/protectedRoutes";
import ProtectedRoute from "./ProtectedRoute";
import LeftPanelLayout from "../layouts/LeftPanelLayout/LeftPanelLayout";
// Lazy load components for code splitting
const LoginPage = React.lazy(() => import("../features/auth/LoginPage"));
const DashboardOverview = React.lazy(
  () => import("../features/dashboard/overview/DashboardOverview")
);
const CampaignListPage = React.lazy(
  () => import("../features/campaign-management/CampaignListPage")
);
const UserListPage = React.lazy(
  () => import("../features/user-management/UserListPage")
);
const RoleListPage = React.lazy(
  () => import("../features/role-management/RoleListPage")
);
const ForgotPasswordPage = React.lazy(
  () => import("../features/auth/ForgotPasswordPage")
);
const ClientManagementPage = React.lazy(
  () => import("../features/client-management/ClientListPage")
);
const UpdateClientLocations = React.lazy(
  () => import("../features/client-management/UpdateClientLocations")
);
const ProfilePage = React.lazy(() => import("../features/profile/ProfilePage"));
const UpdateProfilePage = React.lazy(
  () => import("../features/profile/UpdateProfilePage")
);
const PointHistoryPage = React.lazy(
  () => import("../features/rewards/PointHistory/PointHistoryPage")
);
const PointHistoryInsights = React.lazy(
  () => import("../features/rewards/PointHistory/PointHistoryInsights")
);
const CurrentRankingPage = React.lazy(
  () => import("../features/rewards/CurrentRanking/CurrentRankingPage")
);
const OrderHistoryPage = React.lazy(
  () => import("../features/gift-shop/OrderHistory/OrderHistoryPage")
);
const NoPageFound = React.lazy(
  () => import("../pages/NoPageFound/NoPageFound")
);
const CreateUserPage = React.lazy(
  () => import("../features/user-management/CreateUserPage")
);
const UpdateUserPage = React.lazy(
  () => import("../features/user-management/UpdateUserPage")
);
const UserDetailsPage = React.lazy(
  () => import("../features/user-management/UserDetailsPage")
);
const AdminListPage = React.lazy(
  () => import("../features/admin-management/AdminListPage")
);
const CreateAdminPage = React.lazy(
  () => import("../features/admin-management/CreateAdminPage")
);
const UpdateAdminPage = React.lazy(
  () => import("../features/admin-management/UpdateAdminPage")
);
const AdminDetailsPage = React.lazy(
  () => import("../features/admin-management/AdminDetailsPage")
);
const CreateCampaignPage = React.lazy(
  () => import("../features/campaign-management/CreateCampaignPage")
);
const UpdateCampaignPage = React.lazy(
  () =>
    import("../features/campaign-management/UpdateCampaign/UpdateCampaignPage")
);
const CreateClientPage = React.lazy(
  () => import("../features/client-management/CreateClientPage")
);
const UpdateClientPage = React.lazy(
  () => import("../features/client-management/UpdateClientPage")
);
const UnauthorizedPage = React.lazy(
  () => import("../pages/Unauthorized/UnauthorizedPage")
);
const ClientDetails = React.lazy(
  () => import("../features/client-management/ClientDetails")
);
const CampaignDetailsPage = React.lazy(
  () => import("../features/campaign-management/CampaignDetailsPage")
);
const CreateSurveyPage = React.lazy(
  () => import("../features/survey-management/CreateSurveyPage")
);
const UpdateSurveyPage = React.lazy(
  () => import("../features/survey-management/UpdateSurvey/UpdateSurveyPage")
);
const SurveyDetailsPage = React.lazy(
  () => import("../features/survey-management/SurveyDetailsPage")
);
const SurveyListPage = React.lazy(
  () => import("../features/survey-management/SurveyListPage")
);
const ClientCampaigns = React.lazy(
  () => import("../features/client-management/ClientCampaigns")
);
const RewardSettings = React.lazy(
  () => import("../features/rewards/RewardSettings/RewardSettings")
);
const CreateReward = React.lazy(
  () => import("../features/rewards/RewardSettings/CreateReward")
);
const UpdateReward = React.lazy(
  () => import("../features/rewards/RewardSettings/UpdateReward")
);
const RewardDetails = React.lazy(
  () => import("../features/rewards/RewardSettings/RewardDetails")
);
const CreateGiftCard = React.lazy(
  () => import("../features/gift-shop/GiftCard/CreateGiftCard")
);
const GiftCardDetails = React.lazy(
  () => import("../features/gift-shop/GiftCard/GiftCardDetails")
);
const GiftCardListPage = React.lazy(
  () => import("../features/gift-shop/GiftCard/GiftCardListPage")
);
const UpdatePassword = React.lazy(
  () => import("../features/profile/UpdatePassword")
);
const SmartReceiptList = React.lazy(
  () => import("../features/rewards/SmartReceipt/SmartReceiptList")
);
const CreateSmartReceipt = React.lazy(
  () => import("../features/rewards/SmartReceipt/CreateSmartReceipt")
);
const UpdateSmartReceipt = React.lazy(
  () => import("../features/rewards/SmartReceipt/UpdateSmartReceipt")
);
const SmartReceiptDetails = React.lazy(
  () => import("../features/rewards/SmartReceipt/SmartReceiptDetails")
);
const LocationMatchingReport = React.lazy(
  () =>
    import(
      "../features/reporting/LocationMatchingReport/LocationMatchingReport"
    )
);
const CampaignInsights = React.lazy(
  () => import("../features/reporting/LocationMatchingReport/CampaignInsights")
);
const AudioMatchingReport = React.lazy(
  () => import("../features/reporting/AudioMatchingReport/AudioMatchingReport")
);
const AudioCampaignInsights = React.lazy(
  () =>
    import("../features/reporting/AudioMatchingReport/AudioCampaignInsights")
);
const StoreMatchingReport = React.lazy(
  () => import("../features/reporting/StoreMatchingReport/StoreMatchingReport")
);
const StoreCampaignInsight = React.lazy(
  () => import("../features/reporting/StoreMatchingReport/StoreCampaignInsight")
);
const CampaignReach = React.lazy(
  () => import("../features/dashboard/campaign-reach/CampaignReach")
);
const ShopLift = React.lazy(
  () => import("../features/dashboard/shop-lift/ShopLift")
);
const BrandLift = React.lazy(
  () => import("../features/dashboard/brand-lift/BrandLift")
);
const Receipt = React.lazy(
  () => import("../features/dashboard/receipt/Receipt")
);
const SurveyInsights = React.lazy(
  () => import("../features/dashboard/survey-insights/SurveyInsights")
);
const CampaignEffects = React.lazy(
  () => import("../features/dashboard/campaign-effects/CampaignEffects")
);
const UpdateGiftCard = React.lazy(
  () => import("../features/gift-shop/GiftCard/UpdateGiftCard")
);
const StaticPageSettings = React.lazy(
  () => import("../features/settings/static-pages/StaticPageSettings")
);

// Loading component for Suspense
const LoadingComponent = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "95vh",
    }}
  >
    <Spin size="large" />
  </div>
);

const AppRoutes: React.FC = () => {
  return (
    <Router>
      <Suspense fallback={<LoadingComponent />}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          {/* Protected Routes */}
          {protectedRoutes.map((route, index) => (
            <Route
              key={`${index}-${route.tag}`}
              path={`${route.path}/*`}
              element={
                <ProtectedRoute tag={route.tag}>
                  <LeftPanelLayout>
                    {/* ############## General ############## */}
                    {route.path === "/dashboard/overview" && (
                      <DashboardOverview />
                    )}
                    {route.path === "/dashboard/campaign-effects" && (
                      <CampaignEffects />
                    )}
                    {route.path === "/dashboard/survey-insights" && (
                      <SurveyInsights />
                    )}
                    {route.path === "/dashboard/campaign-reach" && (
                      <CampaignReach />
                    )}
                    {route.path === "/dashboard/shop-lift" && (
                      <ShopLift />
                    )}
                    {route.path === "/dashboard/brand-lift" && (
                      <BrandLift />
                    )}
                    {route.path === "/dashboard/receipt" && (
                      <Receipt />
                    )}
                    {/* Rewards */}
                    {route.path === "/rewards/reward-settings" && (
                      <RewardSettings />
                    )}
                    {route.path === "/rewards/reward-settings/add-reward" && (
                      <CreateReward />
                    )}
                    {route.path ===
                      "/rewards/reward-settings/update-reward/:id" && (
                      <UpdateReward />
                    )}
                    {route.path ===
                      "/rewards/reward-settings/reward-details/:id" && (
                      <RewardDetails />
                    )}
                    {route.path === "/rewards/current-ranking" && (
                      <CurrentRankingPage />
                    )}
                    {route.path === "/rewards/point-history" && (
                      <PointHistoryPage />
                    )}
                    {route.path === "/rewards/point-history/:id" && (
                      <PointHistoryInsights />
                    )}
                    {/* Receipt Management */}
                    {route.path === "/receipt-management" && (
                      <SmartReceiptList />
                    )}
                    {route.path === "/receipt-management/add-receipt" && (
                      <CreateSmartReceipt />
                    )}
                    {route.path ===
                      "/receipt-management/update-receipt/:id" && (
                      <UpdateSmartReceipt />
                    )}
                    {route.path ===
                      "/receipt-management/receipt-details/:id" && (
                      <SmartReceiptDetails />
                    )}
                    {/* Gift-Shop */}
                    {route.path === "/gift-shop/gift-cards" && (
                      <GiftCardListPage />
                    )}
                    {route.path === "/gift-shop/add-gift-card" && (
                      <CreateGiftCard />
                    )}
                    {route.path === "/gift-shop/gift-card-details/:id" && (
                      <GiftCardDetails />
                    )}
                    {route.path === "/gift-shop/update-gift-card/:id" && (
                      <UpdateGiftCard />
                    )}
                    {route.path === "/gift-shop/order-history" && (
                      <OrderHistoryPage />
                    )}
                    {/* Reporting */}
                    {route.path === "/reporting/location-matching-report" && (
                      <LocationMatchingReport />
                    )}
                    {route.path ===
                      "/reporting/location-matching-report/:campaignId" && (
                      <CampaignInsights />
                    )}
                    {route.path === "/reporting/audio-matching-report" && (
                      <AudioMatchingReport />
                    )}
                    {route.path ===
                      "/reporting/audio-matching-report/:campaignId" && (
                      <AudioCampaignInsights />
                    )}
                    {route.path === "/reporting/store-matching-report" && (
                      <StoreMatchingReport />
                    )}
                    {route.path ===
                      "/reporting/store-matching-report/:campaignId" && (
                      <StoreCampaignInsight />
                    )}
                    {/* ############## Management ############## */}
                    {/* Mobile Users */}
                    {route.path === "/users" && <UserListPage />}
                    {route.path === "/users/add-user" && <CreateUserPage />}
                    {route.path === "/users/update-user/:id" && (
                      <UpdateUserPage />
                    )}
                    {route.path === "/users/user-details/:id" && (
                      <UserDetailsPage />
                    )}
                    {/* Admins */}
                    {route.path === "/admins" && <AdminListPage />}
                    {route.path === "/admins/add-admin" && <CreateAdminPage />}
                    {route.path === "/admins/update-admin/:id" && (
                      <UpdateAdminPage />
                    )}
                    {route.path === "/admins/admin-details/:id" && (
                      <AdminDetailsPage />
                    )}
                    {/* Roles */}
                    {route.path === "/roles" && <RoleListPage />}
                    {/* Campaigns */}
                    {route.path === "/campaigns" && <CampaignListPage />}
                    {route.path === "/campaigns/create-campaign" && (
                      <CreateCampaignPage />
                    )}
                    {route.path === "/campaigns/update-campaign/:id" && (
                      <UpdateCampaignPage />
                    )}
                    {route.path === "/campaigns/campaign-details/:id" && (
                      <CampaignDetailsPage />
                    )}
                    {/* Clients */}
                    {route.path === "/clients" && <ClientManagementPage />}
                    {route.path === "/clients/client-details/:id" && (
                      <ClientDetails />
                    )}
                    {route.path === "/clients/add-client" && (
                      <CreateClientPage />
                    )}
                    {route.path === "/clients/update-client/:id" && (
                      <UpdateClientPage />
                    )}
                    {route.path === "/clients/update-client-locations/:id" && (
                      <UpdateClientLocations />
                    )}
                    {route.path === "/clients/:id/campaigns" && (
                      <ClientCampaigns />
                    )}
                    {route.path ===
                      "/clients/:id/campaigns/campaign-details/:id" && (
                      <CampaignDetailsPage />
                    )}
                    {/* Surveys */}
                    {route.path === "/surveys" && <SurveyListPage />}
                    {route.path === "/surveys/create-survey" && (
                      <CreateSurveyPage />
                    )}
                    {route.path === "/surveys/update-survey/:id" && (
                      <UpdateSurveyPage />
                    )}
                    {route.path === "/surveys/survey-details/:id" && (
                      <SurveyDetailsPage />
                    )}
                    {/* ############## Preferences ############## */}
                    {route.path === "/profile" && <ProfilePage />}
                    {route.path === "/profile/edit" && <UpdateProfilePage />}
                    {route.path === "/profile/reset-password" && (
                      <UpdatePassword />
                    )}
                    {route.path === "/settings/static-pages" && (
                      <StaticPageSettings />
                    )}
                  </LeftPanelLayout>
                </ProtectedRoute>
              }
            />
          ))}
          {/* Fallback route */}
          <Route path="*" element={<NoPageFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;

// import React, { Suspense } from "react";
// import {
//   BrowserRouter as Router,
//   Routes,
//   Route,
//   Navigate,
// } from "react-router-dom";
// import { Spin } from "antd";
// // Internal Imports
// import { protectedRoutes } from "../utils/protectedRoutes";
// import ProtectedRoute from "./ProtectedRoute";
// import LeftPanelLayout from "../layouts/LeftPanelLayout/LeftPanelLayout";
// // Lazy load components for code splitting
// const LoginPage = React.lazy(() => import("../features/auth/LoginPage"));
// // COMMENTED OUT - Will be restored later
// // const DashboardOverview = React.lazy(
// //   () => import("../features/dashboard/overview/DashboardOverview")
// // );
// const CampaignListPage = React.lazy(
//   () => import("../features/campaign-management/CampaignListPage")
// );
// const UserListPage = React.lazy(
//   () => import("../features/user-management/UserListPage")
// );
// const RoleListPage = React.lazy(
//   () => import("../features/role-management/RoleListPage")
// );
// const ForgotPasswordPage = React.lazy(
//   () => import("../features/auth/ForgotPasswordPage")
// );
// const ClientManagementPage = React.lazy(
//   () => import("../features/client-management/ClientListPage")
// );
// const UpdateClientLocations = React.lazy(
//   () => import("../features/client-management/UpdateClientLocations")
// );
// const ProfilePage = React.lazy(() => import("../features/profile/ProfilePage"));
// const UpdateProfilePage = React.lazy(
//   () => import("../features/profile/UpdateProfilePage")
// );
// const PointHistoryPage = React.lazy(
//   () => import("../features/rewards/PointHistory/PointHistoryPage")
// );
// const PointHistoryInsights = React.lazy(
//   () => import("../features/rewards/PointHistory/PointHistoryInsights")
// );
// const CurrentRankingPage = React.lazy(
//   () => import("../features/rewards/CurrentRanking/CurrentRankingPage")
// );
// const OrderHistoryPage = React.lazy(
//   () => import("../features/gift-shop/OrderHistory/OrderHistoryPage")
// );
// const NoPageFound = React.lazy(
//   () => import("../pages/NoPageFound/NoPageFound")
// );
// const CreateUserPage = React.lazy(
//   () => import("../features/user-management/CreateUserPage")
// );
// const UpdateUserPage = React.lazy(
//   () => import("../features/user-management/UpdateUserPage")
// );
// const UserDetailsPage = React.lazy(
//   () => import("../features/user-management/UserDetailsPage")
// );
// const AdminListPage = React.lazy(
//   () => import("../features/admin-management/AdminListPage")
// );
// const CreateAdminPage = React.lazy(
//   () => import("../features/admin-management/CreateAdminPage")
// );
// const UpdateAdminPage = React.lazy(
//   () => import("../features/admin-management/UpdateAdminPage")
// );
// const AdminDetailsPage = React.lazy(
//   () => import("../features/admin-management/AdminDetailsPage")
// );
// const CreateCampaignPage = React.lazy(
//   () => import("../features/campaign-management/CreateCampaignPage")
// );
// const UpdateCampaignPage = React.lazy(
//   () =>
//     import("../features/campaign-management/UpdateCampaign/UpdateCampaignPage")
// );
// const CreateClientPage = React.lazy(
//   () => import("../features/client-management/CreateClientPage")
// );
// const UpdateClientPage = React.lazy(
//   () => import("../features/client-management/UpdateClientPage")
// );
// const UnauthorizedPage = React.lazy(
//   () => import("../pages/Unauthorized/UnauthorizedPage")
// );
// const ClientDetails = React.lazy(
//   () => import("../features/client-management/ClientDetails")
// );
// const CampaignDetailsPage = React.lazy(
//   () => import("../features/campaign-management/CampaignDetailsPage")
// );
// const CreateSurveyPage = React.lazy(
//   () => import("../features/survey-management/CreateSurveyPage")
// );
// const UpdateSurveyPage = React.lazy(
//   () => import("../features/survey-management/UpdateSurvey/UpdateSurveyPage")
// );
// const SurveyDetailsPage = React.lazy(
//   () => import("../features/survey-management/SurveyDetailsPage")
// );
// const SurveyListPage = React.lazy(
//   () => import("../features/survey-management/SurveyListPage")
// );
// const ClientCampaigns = React.lazy(
//   () => import("../features/client-management/ClientCampaigns")
// );
// const RewardSettings = React.lazy(
//   () => import("../features/rewards/RewardSettings/RewardSettings")
// );
// const CreateReward = React.lazy(
//   () => import("../features/rewards/RewardSettings/CreateReward")
// );
// const UpdateReward = React.lazy(
//   () => import("../features/rewards/RewardSettings/UpdateReward")
// );
// const RewardDetails = React.lazy(
//   () => import("../features/rewards/RewardSettings/RewardDetails")
// );
// const CreateGiftCard = React.lazy(
//   () => import("../features/gift-shop/GiftCard/CreateGiftCard")
// );
// const GiftCardDetails = React.lazy(
//   () => import("../features/gift-shop/GiftCard/GiftCardDetails")
// );
// const GiftCardListPage = React.lazy(
//   () => import("../features/gift-shop/GiftCard/GiftCardListPage")
// );
// const UpdatePassword = React.lazy(
//   () => import("../features/profile/UpdatePassword")
// );
// const SmartReceiptList = React.lazy(
//   () => import("../features/rewards/SmartReceipt/SmartReceiptList")
// );
// const CreateSmartReceipt = React.lazy(
//   () => import("../features/rewards/SmartReceipt/CreateSmartReceipt")
// );
// const UpdateSmartReceipt = React.lazy(
//   () => import("../features/rewards/SmartReceipt/UpdateSmartReceipt")
// );
// const SmartReceiptDetails = React.lazy(
//   () => import("../features/rewards/SmartReceipt/SmartReceiptDetails")
// );
// const LocationMatchingReport = React.lazy(
//   () =>
//     import(
//       "../features/reporting/LocationMatchingReport/LocationMatchingReport"
//     )
// );
// const CampaignInsights = React.lazy(
//   () => import("../features/reporting/LocationMatchingReport/CampaignInsights")
// );
// const AudioMatchingReport = React.lazy(
//   () => import("../features/reporting/AudioMatchingReport/AudioMatchingReport")
// );
// const AudioCampaignInsights = React.lazy(
//   () =>
//     import("../features/reporting/AudioMatchingReport/AudioCampaignInsights")
// );
// const StoreMatchingReport = React.lazy(
//   () => import("../features/reporting/StoreMatchingReport/StoreMatchingReport")
// );
// const StoreCampaignInsight = React.lazy(
//   () => import("../features/reporting/StoreMatchingReport/StoreCampaignInsight")
// );
// // COMMENTED OUT - Will be restored later
// // const CampaignReach = React.lazy(
// //   () => import("../features/dashboard/campaign-reach/CampaignReach")
// // );
// // const ShopLift = React.lazy(
// //   () => import("../features/dashboard/shop-lift/ShopLift")
// // );
// // const BrandLift = React.lazy(
// //   () => import("../features/dashboard/brand-lift/BrandLift")
// // );
// // const Receipt = React.lazy(
// //   () => import("../features/dashboard/receipt/Receipt")
// // );
// // const SurveyInsights = React.lazy(
// //   () => import("../features/dashboard/survey-insights/SurveyInsights")
// // );
// // const CampaignEffects = React.lazy(
// //   () => import("../features/dashboard/campaign-effects/CampaignEffects")
// // );
// // TEMPORARY PLACEHOLDER
// const DashboardPlaceholder = React.lazy(
//   () => import("../features/dashboard/DashboardPlaceholder")
// );
// const UpdateGiftCard = React.lazy(
//   () => import("../features/gift-shop/GiftCard/UpdateGiftCard")
// );
// const StaticPageSettings = React.lazy(
//   () => import("../features/settings/static-pages/StaticPageSettings")
// );

// // Loading component for Suspense
// const LoadingComponent = () => (
//   <div
//     style={{
//       display: "flex",
//       justifyContent: "center",
//       alignItems: "center",
//       height: "95vh",
//     }}
//   >
//     <Spin size="large" />
//   </div>
// );

// const AppRoutes: React.FC = () => {
//   return (
//     <Router>
//       <Suspense fallback={<LoadingComponent />}>
//         <Routes>
//           <Route path="/" element={<Navigate to="/login" replace />} />
//           <Route path="/login" element={<LoginPage />} />
//           <Route path="/forgot-password" element={<ForgotPasswordPage />} />
//           <Route path="/unauthorized" element={<UnauthorizedPage />} />
//           {/* Protected Routes */}
//           {protectedRoutes.map((route, index) => (
//             <Route
//               key={`${index}-${route.tag}`}
//               path={`${route.path}/*`}
//               element={
//                 <ProtectedRoute tag={route.tag}>
//                   <LeftPanelLayout>
//                     {/* ############## General ############## */}
//                     {/* COMMENTED OUT - Will be restored later */}
//                     {/* {route.path === "/dashboard/overview" && (
//                       <DashboardOverview />
//                     )} */}
//                     {/* {route.path === "/dashboard/campaign-effects" && (
//                       <CampaignEffects />
//                     )} */}
//                     {/* {route.path === "/dashboard/survey-insights" && (
//                       <SurveyInsights />
//                     )} */}
//                     {/* {route.path === "/dashboard/campaign-reach" && (
//                       <CampaignReach />
//                     )} */}
//                     {/* {route.path === "/dashboard/shop-lift" && (
//                       <ShopLift />
//                     )} */}
//                     {/* {route.path === "/dashboard/brand-lift" && (
//                       <BrandLift />
//                     )} */}
//                     {/* {route.path === "/dashboard/receipt" && (
//                       <Receipt />
//                     )} */}
//                     {/* TEMPORARY PLACEHOLDER - Replace the above routes */}
//                     {(route.path === "/dashboard/overview" ||
//                       route.path === "/dashboard/campaign-reach" ||
//                       route.path === "/dashboard/shop-lift" ||
//                       route.path === "/dashboard/brand-lift" ||
//                       route.path === "/dashboard/receipt" ||
//                       route.path === "/dashboard/survey-insights") && (
//                       <DashboardPlaceholder />
//                     )}
//                     {/* Rewards */}
//                     {route.path === "/rewards/reward-settings" && (
//                       <RewardSettings />
//                     )}
//                     {route.path === "/rewards/reward-settings/add-reward" && (
//                       <CreateReward />
//                     )}
//                     {route.path ===
//                       "/rewards/reward-settings/update-reward/:id" && (
//                       <UpdateReward />
//                     )}
//                     {route.path ===
//                       "/rewards/reward-settings/reward-details/:id" && (
//                       <RewardDetails />
//                     )}
//                     {route.path === "/rewards/current-ranking" && (
//                       <CurrentRankingPage />
//                     )}
//                     {route.path === "/rewards/point-history" && (
//                       <PointHistoryPage />
//                     )}
//                     {route.path === "/rewards/point-history/:id" && (
//                       <PointHistoryInsights />
//                     )}
//                     {/* Receipt Management */}
//                     {route.path === "/receipt-management" && (
//                       <SmartReceiptList />
//                     )}
//                     {route.path === "/receipt-management/add-receipt" && (
//                       <CreateSmartReceipt />
//                     )}
//                     {route.path ===
//                       "/receipt-management/update-receipt/:id" && (
//                       <UpdateSmartReceipt />
//                     )}
//                     {route.path ===
//                       "/receipt-management/receipt-details/:id" && (
//                       <SmartReceiptDetails />
//                     )}
//                     {/* Gift-Shop */}
//                     {route.path === "/gift-shop/gift-cards" && (
//                       <GiftCardListPage />
//                     )}
//                     {route.path === "/gift-shop/add-gift-card" && (
//                       <CreateGiftCard />
//                     )}
//                     {route.path === "/gift-shop/gift-card-details/:id" && (
//                       <GiftCardDetails />
//                     )}
//                     {route.path === "/gift-shop/update-gift-card/:id" && (
//                       <UpdateGiftCard />
//                     )}
//                     {route.path === "/gift-shop/order-history" && (
//                       <OrderHistoryPage />
//                     )}
//                     {/* Reporting */}
//                     {route.path === "/reporting/location-matching-report" && (
//                       <LocationMatchingReport />
//                     )}
//                     {route.path ===
//                       "/reporting/location-matching-report/:campaignId" && (
//                       <CampaignInsights />
//                     )}
//                     {route.path === "/reporting/audio-matching-report" && (
//                       <AudioMatchingReport />
//                     )}
//                     {route.path ===
//                       "/reporting/audio-matching-report/:campaignId" && (
//                       <AudioCampaignInsights />
//                     )}
//                     {route.path === "/reporting/store-matching-report" && (
//                       <StoreMatchingReport />
//                     )}
//                     {route.path ===
//                       "/reporting/store-matching-report/:campaignId" && (
//                       <StoreCampaignInsight />
//                     )}
//                     {/* ############## Management ############## */}
//                     {/* Mobile Users */}
//                     {route.path === "/users" && <UserListPage />}
//                     {route.path === "/users/add-user" && <CreateUserPage />}
//                     {route.path === "/users/update-user/:id" && (
//                       <UpdateUserPage />
//                     )}
//                     {route.path === "/users/user-details/:id" && (
//                       <UserDetailsPage />
//                     )}
//                     {/* Admins */}
//                     {route.path === "/admins" && <AdminListPage />}
//                     {route.path === "/admins/add-admin" && <CreateAdminPage />}
//                     {route.path === "/admins/update-admin/:id" && (
//                       <UpdateAdminPage />
//                     )}
//                     {route.path === "/admins/admin-details/:id" && (
//                       <AdminDetailsPage />
//                     )}
//                     {/* Roles */}
//                     {route.path === "/roles" && <RoleListPage />}
//                     {/* Campaigns */}
//                     {route.path === "/campaigns" && <CampaignListPage />}
//                     {route.path === "/campaigns/create-campaign" && (
//                       <CreateCampaignPage />
//                     )}
//                     {route.path === "/campaigns/update-campaign/:id" && (
//                       <UpdateCampaignPage />
//                     )}
//                     {route.path === "/campaigns/campaign-details/:id" && (
//                       <CampaignDetailsPage />
//                     )}
//                     {/* Clients */}
//                     {route.path === "/clients" && <ClientManagementPage />}
//                     {route.path === "/clients/client-details/:id" && (
//                       <ClientDetails />
//                     )}
//                     {route.path === "/clients/add-client" && (
//                       <CreateClientPage />
//                     )}
//                     {route.path === "/clients/update-client/:id" && (
//                       <UpdateClientPage />
//                     )}
//                     {route.path === "/clients/update-client-locations/:id" && (
//                       <UpdateClientLocations />
//                     )}
//                     {route.path === "/clients/:id/campaigns" && (
//                       <ClientCampaigns />
//                     )}
//                     {route.path ===
//                       "/clients/:id/campaigns/campaign-details/:id" && (
//                       <CampaignDetailsPage />
//                     )}
//                     {/* Surveys */}
//                     {route.path === "/surveys" && <SurveyListPage />}
//                     {route.path === "/surveys/create-survey" && (
//                       <CreateSurveyPage />
//                     )}
//                     {route.path === "/surveys/update-survey/:id" && (
//                       <UpdateSurveyPage />
//                     )}
//                     {route.path === "/surveys/survey-details/:id" && (
//                       <SurveyDetailsPage />
//                     )}
//                     {/* ############## Preferences ############## */}
//                     {route.path === "/profile" && <ProfilePage />}
//                     {route.path === "/profile/edit" && <UpdateProfilePage />}
//                     {route.path === "/profile/reset-password" && (
//                       <UpdatePassword />
//                     )}
//                     {route.path === "/settings/static-pages" && (
//                       <StaticPageSettings />
//                     )}
//                   </LeftPanelLayout>
//                 </ProtectedRoute>
//               }
//             />
//           ))}
//           {/* Fallback route */}
//           <Route path="*" element={<NoPageFound />} />
//         </Routes>
//       </Suspense>
//     </Router>
//   );
// };

// export default AppRoutes;